import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/store';
import { APP_VERSION, RESEARCH_USE } from './strings';

/** Persistent banner at the bottom of every screen (desktop: the amber pill at the bottom of every view). */
export function ResearchUseBanner() {
  return (
    <div className="ru-banner" role="note">
      {RESEARCH_USE.headline}
    </div>
  );
}

/** Blocks the app until the signed-in user has accepted the notice for this app version.
 * Mirrors the desktop acknowledge modal: headline, statement, conditions, checkbox, Continue. */
export function ResearchUseGate({ children }: { children: React.ReactNode }) {
  const me = useAuth((s) => s.me);
  const accept = useAuth((s) => s.acceptResearchUse);
  const logout = useAuth((s) => s.logout);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  if (me.research_use.accepted) return <>{children}</>;
  return (
    <div className="modal-backdrop ru-scrim">
      <div className="modal ru-modal" role="dialog" aria-modal="true" aria-labelledby="ru-title">
        <h2 id="ru-title">{RESEARCH_USE.modalTitle}</h2>
        <p className="ru-headline">{RESEARCH_USE.headline}</p>
        <p>{RESEARCH_USE.statementDetail}</p>
        <div className="muted small">{RESEARCH_USE.conditionsTitle}</div>
        <ul className="ru-conditions">
          {RESEARCH_USE.conditions.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <p className="muted small">{RESEARCH_USE.licenseLine}</p>
        <hr />
        <label className="ru-agree">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <strong>{RESEARCH_USE.acknowledgeLabel}</strong>
        </label>
        <p className="muted small">
          Version {me.app_version}. Acceptance is recorded per user and per version.
        </p>
        <hr />
        <div className="row right">
          <button className="secondary" onClick={() => void logout()}>
            Exit
          </button>
          <button
            className="primary-green"
            disabled={!agreed || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await accept();
              } finally {
                setBusy(false);
              }
            }}
          >
            {RESEARCH_USE.continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AboutDialog({ onClose }: { onClose: () => void }) {
  const me = useAuth((s) => s.me);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{RESEARCH_USE.aboutTitle}</h2>
        <table className="kv">
          <tbody>
            <tr>
              <td>Valvia</td>
              <td>{me?.app_version ?? APP_VERSION}</td>
            </tr>
            <tr>
              <td>Manufacturer</td>
              <td>{RESEARCH_USE.manufacturer}</td>
            </tr>
            <tr>
              <td>Signed in as</td>
              <td>
                {me?.user.email} ({me?.user.role})
              </td>
            </tr>
            <tr>
              <td>Notice accepted</td>
              <td>{me?.research_use.accepted_at ?? '-'}</td>
            </tr>
          </tbody>
        </table>
        <hr />
        <p>
          <strong className="ru-headline">{RESEARCH_USE.headline}</strong> {RESEARCH_USE.statementDetail}
        </p>
        <p className="muted small">{RESEARCH_USE.licenseLine}</p>
        <hr />
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/** Sets document.title with the research-use suffix (desktop: the window title). */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = `${title}${RESEARCH_USE.titleSuffix}`;
  }, [title]);
}

